import useSWR from "swr";
import { useTranslation } from "react-i18next";
import { ServicesURL } from "@utils/utilsApi";
import { configurations, PagesID } from "@utils/utilsConfigurations";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";

export interface RuleOption {
  value: string;
  label: string;
}

type FieldCategory = "comparable" | "discrete";

const CONDITIONS_BY_CATEGORY: Record<FieldCategory, string[]> = {
  comparable: [
    "EQUALS",
    "GREATER",
    "LESS",
    "GREATER_EQUALS",
    "LESS_EQUALS",
    "NULL",
  ],
  discrete: ["EQUALS", "INCLUDES", "NULL"],
};

const COMPARABLE_FORM_TYPES = ["datetime", "time", "autoincrement"];
const COMPARABLE_CUSTOM_TYPES = [
  "integer",
  "number",
  "float",
  "decimal",
  "date",
  "datetime",
];

export const useRuleDataset = (corporateId: string) => {
  const { t } = useTranslation("input");
  const url = corporateId
    ? `${ServicesURL.dynamicGroups.replace(
        ":idCorporate",
        corporateId,
      )}/rule-dataset`
    : null;

  const { data } = useSWR(url, (u: string) => fetcher(u, MethodType.GET));

  const toOption = (item: any, groupKey: string): RuleOption => {
    const value = typeof item === "string" ? item : (item?.value ?? item?.key);
    const dsLabel =
      item && typeof item === "object" ? item.label || item.key || "" : "";
    const label = t(`options.${groupKey}.${String(value).toLowerCase()}`, {
      defaultValue: dsLabel || String(value),
    }) as string;
    return { value, label };
  };

  const customFields = (data?.custom_fields as any[]) || [];

  const fieldOptions: RuleOption[] = [
    ...((data?.condition_field as any[]) || []).map((i) =>
      toOption(i, "condition_field-group"),
    ),
    ...(customFields.map((c) => ({
      value: c.key,
      label: c.label,
    })) as RuleOption[]),
  ];

  const conditionOptions: RuleOption[] = ((data?.condition as any[]) || []).map(
    (i) => toOption(i, "condition-group"),
  );

  const userFormColumns: any[] =
    configurations[PagesID["corporate.groups-dynamic.users.form"]]?.columns ||
    [];

  const resolveCategory = (fieldValue: string): FieldCategory | null => {
    const key = String(fieldValue).toLowerCase();

    const stdCol = userFormColumns.find(
      (c) => String(c.accessorKey).toLowerCase() === key,
    );
    if (stdCol) {
      const isComparable =
        String(stdCol.inpuType).toLowerCase() === "number" ||
        COMPARABLE_FORM_TYPES.includes(String(stdCol.type).toLowerCase());
      return isComparable ? "comparable" : "discrete";
    }

    const custom = customFields.find(
      (c) => String(c.key).toLowerCase() === key,
    );
    if (custom) {
      const customType = String(
        custom.type ?? custom.field_type ?? custom.data_type ?? "",
      ).toLowerCase();
      return COMPARABLE_CUSTOM_TYPES.includes(customType)
        ? "comparable"
        : "discrete";
    }

    return null;
  };

  const getConditionOptions = (fieldValue?: string): RuleOption[] => {
    if (!fieldValue) return conditionOptions;
    const category = resolveCategory(fieldValue);
    if (!category) return conditionOptions;
    const allowed = CONDITIONS_BY_CATEGORY[category];
    return conditionOptions.filter((o) =>
      allowed.includes(String(o.value).toUpperCase()),
    );
  };

  return { fieldOptions, conditionOptions, getConditionOptions };
};
